import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";
import {
  LayoutDashboard,
  UtensilsCrossed,
  Menu as MenuIcon,
  ReceiptText,
  FileBarChart,
  Settings as SettingsIcon,
  LogOut,
  Building2,
  User,
  Lock,
  X // Added X icon for closing mobile sidebar
} from "lucide-react";
import { ChevronRight, ChevronLeft } from "lucide-react";
import logo from "../assets/lg.png";
import "./Settings.css";

function Settings() {
  const navigate = useNavigate();

  // --- ADDED: State for Mobile Sidebar ---
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Initial State
  const [hotelData, setHotelData] = useState({
    name: "",
    contact: "",
    address: "",
    username: "",
    email: "",
    password: "" 
  });

  const [newPassword, setNewPassword] = useState("");
  const [currentPassInput, setCurrentPassInput] = useState("");

  // 1. Fetch data from MySQL on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/settings.php`);
        if (res.data) {
          setHotelData(res.data);
        }
      } catch (err) {
        console.error("Failed to load settings from database:", err);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    
    // 2. Password Change Logic
    let updatedPassword = hotelData.password;

    if (newPassword.trim() !== "") {
      // Compare user input with the password fetched from the database
      if (currentPassInput !== hotelData.password) {
        alert("Verification failed: Current password does not match!");
        return;
      }
      updatedPassword = newPassword;
    }

    const updatedData = { 
      ...hotelData, 
      password: updatedPassword 
    };

    try {
      // 3. Save to Database via PHP
      const res = await axios.post(`${API_BASE_URL}/settings.php`, updatedData);
      
      if (res.data.status === "success") {
        setHotelData(updatedData);
        setNewPassword("");
        setCurrentPassInput("");
        alert("Settings updated successfully in database!");
      } else {
        alert("Error updating settings: " + res.data.message);
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("Server error. Please check if XAMPP is running.");
    }
  };

  return (
    <div className="dash-container">
      {/* --- SIDEBAR (Updated with Mobile Active Class) --- */}
      <aside className={`sidebar ${isSidebarOpen ? 'active' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>

  {/* COLLAPSE BUTTON */}
  <button 
    className="sidebar-toggle-btn d-none d-md-flex"
    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
  >
    {isSidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
  </button>

  <div className="sidebar-top">

    {/* ✅ LOGO (same as Dashboard) */}
    <div className="brand">
      <img src={logo} alt="Logo" className="sidebar-logo" />

      {/* Mobile Close */}
      <X 
        className="d-md-none ms-auto cursor-pointer" 
        onClick={() => setIsSidebarOpen(false)} 
      />
    </div>

    {/* NAVIGATION */}
    <nav className="nav-menu">
      <div className="nav-item" onClick={() => navigate("/dashboard")}>
        <LayoutDashboard size={20} /> {!isSidebarCollapsed && "Dashboard"}
      </div>

      <div className="nav-item" onClick={() => navigate("/tables")}>
        <UtensilsCrossed size={20} /> {!isSidebarCollapsed && "Tables"}
      </div>

      <div className="nav-item" onClick={() => navigate("/menu")}>
        <MenuIcon size={20} /> {!isSidebarCollapsed && "Menu"}
      </div>

      <div className="nav-item" onClick={() => navigate("/billing")}>
        <ReceiptText size={20} /> {!isSidebarCollapsed && "Billing"}
      </div>

      <div className="nav-item" onClick={() => navigate("/reports")}>
        <FileBarChart size={20} /> {!isSidebarCollapsed && "Reports"}
      </div>
    </nav>
  </div>

  {/* BOTTOM */}
  <div className="sidebar-bottom">
    <div className="nav-item active" onClick={() => navigate("/settings")}>
      <SettingsIcon size={20} /> {!isSidebarCollapsed && "Settings"}
    </div>

    <div className="nav-item logout-red" onClick={() => navigate("/")}>
      <LogOut size={20} /> {!isSidebarCollapsed && "Logout"}
    </div>
  </div>

</aside>

      {/* --- MAIN CONTENT --- */}
      <main className="main-content">
        
        {/* --- ADDED: MOBILE TOP BAR (3 lines and Center Name) --- */}
        <div className="mobile-top-bar d-md-none">
          <div className="brand-text">Dine Spark</div>
          <MenuIcon className="cursor-pointer" onClick={() => setIsSidebarOpen(true)} />
        </div>

        <header className="page-header">
          <div className="header-titles">
            <h1 className="serif-title">Settings</h1>
            <p className="subtitle">Manage hotel database settings</p>
          </div>
        </header>

        <div className="settings-container">
          <div className="settings-card">
            <form onSubmit={handleSave}>

              {/* Hotel Details Section */}
              <div className="settings-section">
                <div className="section-title">
                  <Building2 size={18} className="icon-orange"/>
                  Hotel Details
                </div>
                <div className="input-row">
                  <div className="input-box">
                    <label>Hotel Name</label>
                    <input
                      type="text"
                      value={hotelData.name}
                      onChange={(e)=>setHotelData({...hotelData, name:e.target.value})}
                    />
                  </div>
                  <div className="input-box">
                    <label>Contact Number</label>
                    <input
                      type="text"
                      value={hotelData.contact}
                      onChange={(e)=>setHotelData({...hotelData, contact:e.target.value})}
                    />
                  </div>
                </div>
                <div className="input-box full-width">
                  <label>Address</label>
                  <input
                    type="text"
                    value={hotelData.address}
                    onChange={(e)=>setHotelData({...hotelData, address:e.target.value})}
                  />
                </div>
              </div>

              {/* Admin Account Section */}
              <div className="settings-section">
                <div className="section-title">
                  <User size={18} className="icon-orange"/>
                  Admin Account
                </div>
                <div className="input-row">
                  <div className="input-box">
                    <label>Username</label>
                    <input
                      type="text"
                      value={hotelData.username}
                      onChange={(e)=>setHotelData({...hotelData, username:e.target.value})}
                    />
                  </div>
                  <div className="input-box">
                    <label>Email</label>
                    <input
                      type="email"
                      value={hotelData.email}
                      onChange={(e)=>setHotelData({...hotelData, email:e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Change Password Section */}
              <div className="settings-section">
                <div className="section-title">
                  <Lock size={18} className="icon-orange"/>
                  Change Password
                </div>
                <div className="input-row">
                  <div className="input-box">
                    <label>Current Password</label>
                    <input 
                      type="password"
                      placeholder="Verify current password"
                      value={currentPassInput}
                      onChange={(e) => setCurrentPassInput(e.target.value)}
                    />
                  </div>
                  <div className="input-box">
                    <label>New Password</label>
                    <input 
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-footer">
                <button type="submit" className="btn-save">Save Changes</button>
              </div>

            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Settings;